import { useState, useEffect, useContext } from "react";
import { X, MessageSquare, Circle } from "lucide-react";
import axios from "axios";
import { API, AuthContext } from "../App";

const PrivateMessagesPanel = ({ onClose, onOpenPM, fullWidth = false }) => {
  const { token } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await axios.get(`${API}/messages/conversations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const sorted = (response.data || []).slice().sort(
          (a, b) => new Date(b.last_message?.timestamp || 0) - new Date(a.last_message?.timestamp || 0)
        );
        setConversations(sorted);
      } catch (error) {
        console.error("Failed to fetch conversations:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [token]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`${fullWidth ? 'w-full' : 'w-64'} retro-window`} data-testid="messages-panel">
      <div className="retro-title-bar">
        <span className="flex items-center gap-1">
          <MessageSquare className="w-3 h-3" />
          Messages
        </span>
        <button 
          className="close-button-visible"
          onClick={onClose}
          data-testid="close-messages-btn"
          title="Close"
        >
          ✕
        </button>
      </div>

      <div className="p-2 bg-[#C0C0C0]">
        <div className="retro-inset h-72 overflow-y-auto retro-scrollbar">
          {loading ? (
            <p className="text-xs text-center text-[#808080] p-4">Loading...</p>
          ) : conversations.length === 0 ? (
            <p className="text-xs text-center text-[#808080] p-4">No messages yet</p>
          ) : (
            <div className="p-1">
              {conversations.map((conv) => (
                <div 
                  key={conv.other_user_id} 
                  className="p-2 hover:bg-[#E0E0E0] cursor-pointer border-b border-[#E0E0E0]"
                  data-testid={`conversation-${conv.other_user_id}`}
                  onDoubleClick={() => {
                    if (onOpenPM) {
                      onOpenPM({
                        user_id: conv.other_user_id,
                        username: conv.other_user?.username || conv.other_username || "User"
                      });
                    }
                  }}
                  title="Double-click to open chat"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-6 h-6 rounded-sm flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: conv.other_user?.avatar_color || "#7B0099" }}
                      >
                        {conv.other_user?.username?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-arial text-xs font-bold">{conv.other_user?.username}</p>
                        <p className="font-arial text-xs text-[#808080] truncate max-w-32">
                          {conv.last_message?.content}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-arial text-xs text-[#808080]">
                        {formatTime(conv.last_message?.timestamp)}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="inline-flex items-center justify-center w-4 h-4 bg-[#7B0099] text-white text-xs rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivateMessagesPanel;
